import { ReactNode, useCallback, useMemo } from 'react'

import { Form } from 'antd'
import { Rule } from 'antd/es/form'
import { NamePath } from 'antd/es/form/interface'
import { T } from 'govwell-ui'
import { styled } from 'styled-components'

import GovWellUIWrapper from './GovWellUIWrapper'
import { Label } from './Label'
import { CaptionContainer } from './StyledComponents'
import { makeFieldNameWithPrefix, WrapperChildParams } from './util'

export interface FormFieldWrapperProps<InputType = any> {
  ariaLabel?: string
  label?: ReactNode
  fieldName: NamePath
  errorMessage?: string
  required?: boolean | null
  caption?: ReactNode
  noMargin?: boolean
  tooltip?: string | null
  rules?: Rule[]
  initialValue?: InputType
  valuePropName?: string
  width?: string
  minWidth?: React.CSSProperties['minWidth']
  disabled?: boolean
  noValidation?: boolean
  fieldNamePrefix?: NamePath // the top-level item for form parent fields i.e. <Form.List name="fieldNamePrefix">
  isTestMode?: boolean
  /** When true, suppresses the wrapper's visual required mark; use when the label node already includes an asterisk via `FieldLabel`. */
  hideRequiredAsterisk?: boolean
}

interface Props<InputType> extends FormFieldWrapperProps<InputType> {
  children: ReactNode | ((params: WrapperChildParams) => ReactNode)
  /**
   * Using this to hide error message when it's going to be presented by a govwell-ui FormControl instead;
   * there's no config on Form.Item to suppress the display of this when an error is present
   */
  isGovWellUI?: boolean
}

interface ContainerProps {
  $hideErrorMessage?: boolean
  $width: string | undefined
  $minWidth?: React.CSSProperties['minWidth']
}
const Container = styled.div<ContainerProps>`
  width: ${(props) => props.$width || '100%'};
  min-width: ${(props) => props.$minWidth || 'auto'};
  .ant-form-item-explain-error {
    margin: 8px 0 24px;
  }
  .ant-switch-handle::before {
    height: 18px;
    width: 18px;
    border-radius: 16px;
  }
  .ant-switch-checked .ant-switch-handle {
    inset-inline-start: calc(100% - 20px);
  }
  .ant-form-item-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .ant-form-item-label {
    padding: 0 0 3px;
  }
  /* AntD's label column shrinks to content; force it (and its inner label)
   * to span the full field width so the contextual help chip can anchor to
   * the label row. */
  .ant-form-item-label:has([data-field-help-zone]) {
    overflow: visible;
    width: 100%;
  }
  .ant-form-item-label:has([data-field-help-zone]) > label {
    width: 100%;
  }
  .ant-form-item-required span {
    line-height: 20px;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
    ${({ $hideErrorMessage }) => $hideErrorMessage && 'display: none;'};
  }
`

export const Wrapper = <TInput,>(props: Props<TInput>) => {
  const {
    children,
    fieldName,
    label,
    errorMessage,
    isGovWellUI,
    required,
    caption,
    noMargin,
    tooltip,
    initialValue,
    valuePropName,
    width,
    minWidth,
    noValidation,
    fieldNamePrefix,
    hideRequiredAsterisk,
  } = props
  const getRules = useCallback(() => {
    if (noValidation) {
      return []
    }
    if (props.rules) {
      return props.rules
    }
    return errorMessage ? [{ required: true, message: errorMessage }] : []
  }, [errorMessage, noValidation, props.rules])
  const rules = getRules()

  const form = Form.useFormInstance()

  const fullyQualifiedFieldName: NamePath = useMemo(
    () => makeFieldNameWithPrefix(fieldName, fieldNamePrefix),
    [fieldName, fieldNamePrefix]
  )
  Form.useWatch(fullyQualifiedFieldName, form)

  const errorToDisplay = form.getFieldError(fullyQualifiedFieldName)?.[0]

  const value = form.getFieldValue(fullyQualifiedFieldName)
  const onValueChange = useCallback(
    (newValue: unknown) => {
      form.setFieldValue(fullyQualifiedFieldName, newValue)
    },
    [fullyQualifiedFieldName, form]
  )

  const shouldUpdate = useCallback(
    (prevValues: Record<string, unknown>, newValues: Record<string, unknown>) => {
      const get = (values: Record<string, unknown>) => {
        if (Array.isArray(fieldName)) {
          return getValueByPath(values, fieldName)
        }
        return values[fieldName]
      }
      return get(prevValues) !== get(newValues)
    },
    [fieldName]
  )

  return (
    <Container
      $hideErrorMessage={isGovWellUI}
      $width={width}
      $minWidth={minWidth}
      id={Array.isArray(fieldName) ? fieldName.join('_') : fieldName}
    >
      <Form.Item
        label={label && <Label label={label} tooltip={tooltip || undefined} />}
        name={fieldName}
        shouldUpdate={shouldUpdate}
        rules={rules}
        {...(initialValue ? { initialValue } : {})}
        {...(valuePropName ? { valuePropName } : {})}
        // When the label node already renders its own asterisk (via `FieldLabel`),
        // `hideRequiredAsterisk` forces antd's `required` to `false` so the custom
        // `requiredMark` render function does not append a second asterisk.
        // Validation is unaffected — it is driven by `rules`/`errorMessage`, not
        // this visual flag. Otherwise `??` (not `||`) lets an explicit
        // `required={false}` reach antd while `undefined` defers to the rules.
        required={hideRequiredAsterisk ? false : (required ?? undefined)}
        extra={
          caption && (
            <CaptionContainer>
              {typeof caption === 'string' ? (
                <T.BodySmall as="span" className="text-neutral-500">
                  {caption}
                </T.BodySmall>
              ) : (
                caption
              )}
            </CaptionContainer>
          )
        }
        style={noMargin ? { margin: 0 } : {}}
      >
        {typeof children === 'function' ? (
          isGovWellUI ? (
            <GovWellUIWrapper
              fieldName={fullyQualifiedFieldName}
              form={form}
              render={children}
              tooltip={tooltip}
            />
          ) : (
            children({ errorMessage: errorToDisplay, onValueChange, value })
          )
        ) : (
          children
        )}
      </Form.Item>
    </Container>
  )
}

export default Wrapper

function getValueByPath(obj: any, path: string[]): any {
  let current = obj
  for (const field of path) {
    if (current?.[field] === undefined) {
      return undefined
    }
    current = current[field]
  }
  return current
}
